const _excluded = ["width"],
  _excluded2 = ["width", "children", "text", "truncation", "truncationOffset", "truncationPosition", "ellipsis", "containerRef", "measurementRenderAPI"];
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from "prop-types";
import { useCombinedRefs } from '../../services';
import { EuiResizeObserver } from '../observer/resize_observer';
import { TruncationUtilsWithDOM, TruncationUtilsWithCanvas } from './utils';
import { euiTextTruncateStyles } from './text_truncate.styles';
var TRUNCATION_TYPES = ['end', 'start', 'startEnd', 'middle'];
export var EuiTextTruncate = function EuiTextTruncate(_ref) {
  var width = _ref.width,
    props = _objectWithoutProperties(_ref, _excluded);
  return width != null ? ___EmotionJSX(EuiTextTruncateWithWidth, _extends({
    width: width
  }, props)) : ___EmotionJSX(EuiTextTruncateWithResizeObserver, props);
};
EuiTextTruncate.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * The full text string to truncate
       */
  text: PropTypes.string.isRequired,
  /**
       * The truncation type desired. Determines where the ellipses are placed.
       */
  truncation: PropTypes.any,
  /**
       * This prop **only** applies to the `start` and `end` truncation types.
       * It allows preserving a certain number of characters of either the
       * starting or ending text.
       *
       * If the passed offset is greater than the total text length,
       * the offset will be ignored.
       */
  truncationOffset: PropTypes.number,
  /**
       * This prop **only** applies to the `startEnd` truncation type.
       * It allows customizing the anchor position of the displayed text,
       * which otherwise defaults to the middle of the text string.
       *
       * The primary use case for this prop for is search highlighting - e.g., if
       * a user searches for a specific word in the text, pass the index of that
       * found word to ensure it is always visible.
       *
       * This behavior will intelligently detect when positions are close to the start
       * or end of the text, and omit leading or trailing ellipses when necessary.
       * If the passed position is greater than the total text length,
       * the truncation will simply default to `start` instead.
       */
  truncationPosition: PropTypes.number,
  /**
       * Defaults to the horizontal ellipsis character.
       * Can be optionally configured to use other punctuation,
       * e.g. spaces, brackets, hyphens, asterisks, etc.
       */
  ellipsis: PropTypes.string,
  /**
       * By default, EuiTextTruncate will render a resize observer to detect the
       * available width it has. For performance reasons (e.g. multiple truncated
       * text items within the same container), you may opt to pass in your own
       * container width, which will skip initializing a resize observer.
       */
  width: PropTypes.number,
  /**
       * Optional callback that fires when the default resizer observer both mounts and
       * registers a size change. This callback will **not** fire if `width` is passed.
       */
  onResize: PropTypes.func,
  /**
       * By default, EuiTextTruncate will calculate its truncation via DOM manipulation
       * and measurement, which has the benefit of automatically inheriting font styles.
       * However, if this approach proves to have a significant performance impact for your
       * usage, consider using the `canvas` API instead, which is more performant.
       *
       * Please note that there are minute pixel to subpixel differences between the
       * two options due to different rendering engines.
       */
  measurementRenderAPI: PropTypes.oneOf(["dom", "canvas"]),
  /**
       * By default, EuiTextTruncate will render the truncated string directly.
       * You can optionally pass a render prop function to the component, which
       * allows for more flexible text rendering, e.g. adding custom markup
       * or highlighting
       */
  children: PropTypes.func
};
var EuiTextTruncateWithWidth = function EuiTextTruncateWithWidth(_ref2) {
  var width = _ref2.width,
    children = _ref2.children,
    text = _ref2.text,
    _ref2$truncation = _ref2.truncation,
    _truncation = _ref2$truncation === void 0 ? 'end' : _ref2$truncation,
    _ref2$truncationOffse = _ref2.truncationOffset,
    _truncationOffset = _ref2$truncationOffse === void 0 ? 0 : _ref2$truncationOffse,
    truncationPosition = _ref2.truncationPosition,
    _ref2$ellipsis = _ref2.ellipsis,
    ellipsis = _ref2$ellipsis === void 0 ? '…' : _ref2$ellipsis,
    containerRef = _ref2.containerRef,
    _ref2$measurementRend = _ref2.measurementRenderAPI,
    measurementRenderAPI = _ref2$measurementRend === void 0 ? 'dom' : _ref2$measurementRend,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  // Note: This needs to be a state and not a ref to trigger a rerender on mount
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    containerEl = _useState2[0],
    setContainerEl = _useState2[1];
  var refs = useCombinedRefs([setContainerEl, containerRef]);

  // Handle exceptions where we need to override the passed props
  var _useMemo = useMemo(function () {
      var truncation = _truncation;
      var truncationOffset = 0;
      if (_truncation === 'end' || _truncation === 'start') {
        if (0 < _truncationOffset && _truncationOffset < text.length) {
          truncationOffset = _truncationOffset;
        }
      } else if (_truncation === 'startEnd' && truncationPosition != null) {
        if (truncationPosition <= 0) {
          truncation = 'end';
        } else if (truncationPosition >= text.length) {
          truncation = 'start';
        }
      }
      return {
        truncation: truncation,
        truncationOffset: truncationOffset
      };
    }, [_truncation, _truncationOffset, truncationPosition, text.length]),
    truncation = _useMemo.truncation,
    truncationOffset = _useMemo.truncationOffset;
  var truncatedText = useMemo(function () {
    var truncatedText = '';
    if (!containerEl || !width) return truncatedText;
    var params = {
      fullText: text,
      ellipsis: ellipsis,
      container: containerEl,
      availableWidth: width
    };
    var utils = measurementRenderAPI === 'canvas' ? new TruncationUtilsWithCanvas(params) : new TruncationUtilsWithDOM(params);
    if (utils.checkIfTruncationIsNeeded() === false) {
      truncatedText = text;
    } else if (utils.checkSufficientEllipsisWidth(truncation) === false) {
      truncatedText = '';
    } else {
      switch (truncation) {
        case 'end':
          truncatedText = utils.truncateEnd(truncationOffset);
          break;
        case 'start':
          truncatedText = utils.truncateStart(truncationOffset);
          break;
        case 'startEnd':
          if (truncationPosition == null) {
            truncatedText = utils.truncateStartEndAtMiddle();
          } else {
            truncatedText = utils.truncateStartEndAtPosition(truncationPosition);
          }
          break;
        case 'middle':
          truncatedText = utils.truncateMiddle();
          break;
      }
    }
    if (measurementRenderAPI === 'dom') {
      utils.cleanup();
    }
    return truncatedText;
  }, [width, text, truncation, truncationOffset, truncationPosition, ellipsis, containerEl, measurementRenderAPI]);
  var isTruncating = truncatedText !== text;
  return ___EmotionJSX("div", _extends({
    css: euiTextTruncateStyles.euiTextTruncate,
    ref: refs,
    title: isTruncating ? text : undefined
  }, rest), isTruncating ? ___EmotionJSX(React.Fragment, null, ___EmotionJSX("span", {
    css: euiTextTruncateStyles.truncatedText,
    "aria-hidden": true,
    "data-test-subj": "truncatedText"
  }, children ? children(truncatedText) : truncatedText), ___EmotionJSX("span", {
    css: euiTextTruncateStyles.fullText,
    "data-test-subj": "fullText"
  }, text)) : ___EmotionJSX("span", {
    "data-test-subj": "fullText"
  }, text));
};
EuiTextTruncateWithWidth.propTypes = {
  width: PropTypes.number.isRequired,
  containerRef: PropTypes.any
};
var EuiTextTruncateWithResizeObserver = ({
  onResize: _onResize,
  ...props
}) => {
  const [width, setWidth] = useState(0);
  const onResize = useCallback(({
    width
  }) => {
    setWidth(width);
    _onResize?.(width);
  }, [_onResize]);
  return <EuiResizeObserver onResize={onResize}>
      {ref => <EuiTextTruncateWithWidth width={width} containerRef={ref} {...props} />}
    </EuiResizeObserver>;
};
try {
  EuiTextTruncate.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/text_truncate/text_truncate.tsx',
    description: '',
    displayName: 'EuiTextTruncate',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      text: {
        defaultValue: null,
        description: 'The full text string to truncate',
        name: 'text',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      truncation: {
        defaultValue: null,
        description: 'The truncation type desired. Determines where the ellipses are placed.',
        name: 'truncation',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"end" | "middle" | "start" | "startEnd"',
          value: [{
            value: '"end"'
          }, {
            value: '"middle"'
          }, {
            value: '"start"'
          }, {
            value: '"startEnd"'
          }]
        }
      },
      truncationOffset: {
        defaultValue: null,
        description: 'This prop **only** applies to the `start` and `end` truncation types.\n' + 'It allows preserving a certain number of characters of either the\n' + 'starting or ending text.\n' + '\n' + 'If the passed offset is greater than the total text length,\n' + 'the offset will be ignored.',
        name: 'truncationOffset',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      truncationPosition: {
        defaultValue: null,
        description: 'This prop **only** applies to the `startEnd` truncation type.\n' + 'It allows customizing the anchor position of the displayed text,\n' + 'which otherwise defaults to the middle of the text string.\n' + '\n' + 'The primary use case for this prop for is search highlighting - e.g., if\n' + 'a user searches for a specific word in the text, pass the index of that\n' + 'found word to ensure it is always visible.\n' + '\n' + 'This behavior will intelligently detect when positions are close to the start\n' + 'or end of the text, and omit leading or trailing ellipses when necessary.\n' + 'If the passed position is greater than the total text length,\n' + 'the truncation will simply default to `start` instead.',
        name: 'truncationPosition',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      ellipsis: {
        defaultValue: {
          value: '…'
        },
        description: 'Defaults to the horizontal ellipsis character.\n' + 'Can be optionally configured to use other punctuation,\n' + 'e.g. spaces, brackets, hyphens, asterisks, etc.',
        name: 'ellipsis',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      width: {
        defaultValue: null,
        description: 'By default, EuiTextTruncate will render a resize observer to detect the\n' + 'available width it has. For performance reasons (e.g. multiple truncated\n' + 'text items within the same container), you may opt to pass in your own\n' + 'container width, which will skip initializing a resize observer.',
        name: 'width',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      onResize: {
        defaultValue: null,
        description: 'Optional callback that fires when the default resizer observer both mounts and\n' + 'registers a size change. This callback will **not** fire if `width` is passed.',
        name: 'onResize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(width: number) => void'
        }
      },
      measurementRenderAPI: {
        defaultValue: {
          value: 'dom'
        },
        description: 'By default, EuiTextTruncate will calculate its truncation via DOM manipulation\n' + 'and measurement, which has the benefit of automatically inheriting font styles.\n' + 'However, if this approach proves to have a significant performance impact for your\n' + 'usage, consider using the `canvas` API instead, which is more performant.\n' + '\n' + 'Please note that there are minute pixel to subpixel differences between the\n' + 'two options due to different rendering engines.',
        name: 'measurementRenderAPI',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"canvas" | "dom"',
          value: [{
            value: '"canvas"'
          }, {
            value: '"dom"'
          }]
        }
      },
      children: {
        defaultValue: null,
        description: 'By default, EuiTextTruncate will render the truncated string directly.\n' + 'You can optionally pass a render prop function to the component, which\n' + 'allows for more flexible text rendering, e.g. adding custom markup\n' + 'or highlighting',
        name: 'children',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/text_truncate/text_truncate.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(truncatedString: string) => ReactNode'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}